import { ELinkVariant, ETextStyleVariant, Link, Text } from '@outdoorsyco/bonfire';
import React, { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useBreakpoint } from '@/hooks/useBreakpoint';

const getSections = (title: string) => [
  {
    title: 'Search by location',
    subtitle: `Tell us where in ${title} you want to pick up or have your RV delivered`,
  },
  {
    title: 'Filter search results',
    subtitle: 'Sort by vehicle type, date, price, and amenities',
  },
  {
    title: 'Message the owner',
    subtitle: 'Learn more about your favorite RV and the best local destinations',
  },
  {
    title: 'Request a booking',
    subtitle: 'Send a request directly to the host and start preparing for your adventure',
  },
];

const formatDescription = (description: string, isAboveDesktop: boolean) => {
  const paragraphRegex = /<p>(.*?)<\/p>/g;
  const linkRegex = /(<a[^>]*>.*?<\/a>)/;

  const matches = description.match(paragraphRegex) || [];

  return matches.map((match, index) => (
    <Text key={index} className="inline">
      {match
        .replace(/<\/?p>/g, '')
        .split(linkRegex)
        .map((part, partIndex) => {
          if (part?.startsWith('<a')) {
            const [, href = ''] = part.match(/href="([^"]*)"/) || [];
            const [, text = ''] = part.match(/>([^<]*)</) || [];
            const cleanHref = href.replace(/^https?:\/\/[^/]+/, '');

            return (
              <Link
                key={partIndex}
                href={cleanHref}
                variant={ELinkVariant.Underline}
                className="hover:underline"
                target={isAboveDesktop ? '_blank' : ''}>
                {text}
              </Link>
            );
          }
          return part;
        })}
      {index !== matches.length - 1 ? ' ' : ''}
    </Text>
  ));
};

const SectionList = memo(({ title }: { title: string }) => (
  <ul className="flex flex-col md:grid gap-6 md:justify-start md:items-start md:grid-cols-2 lg:grid-cols-4">
    {getSections(title).map((section, index) => (
      <li
        key={index}
        className="flex w-full text-center md:items-start md:flex-col lg:items-start lg:text-left">
        <div className="flex justify-center mb-2 md:justify-start lg:justify-start">
          <div className="flex items-center justify-center w-10 h-10 text-xl border-2 border-gray-900 rounded-full">
            {index + 1}
          </div>
        </div>
        <div className="flex flex-col items-start pl-4 gap-2 text-start md:pl-0">
          <h3 className="text-xl font-medium text-gray-900">{section.title}</h3>
          <p className="text-lg text-gray-900">{section.subtitle}</p>
        </div>
      </li>
    ))}
  </ul>
));

SectionList.displayName = 'SectionList';

interface IHowToOutdoorsyProps {
  title?: string;
  description?: string;
}

const HowToOutdoorsy = memo(({ title, description }: IHowToOutdoorsyProps) => {
  const [isFullText, setIsFullText] = useState(false);
  const { isAboveDesktop } = useBreakpoint();

  // Memoize formatted description
  const formattedDescription = description ? formatDescription(description, isAboveDesktop) : null;

  return (
    <div>
      {description && formattedDescription && (
        <div className="pb-6 lg:pb-10">
          <div className={`${isFullText ? '' : 'line-clamp-3'} relative`}>
            <div className="relative">
              {formattedDescription}
              {!isFullText && (
                <span className="absolute bottom-0 right-0 pl-2 bg-canvas-100">
                  <button onClick={() => setIsFullText(true)}>
                    <Text variant={ETextStyleVariant.MediumBold} className="no-underline">
                      <FormattedMessage defaultMessage="Read more..." id="jPuAmf" />
                    </Text>
                  </button>
                </span>
              )}
            </div>
          </div>
          {isFullText && (
            <button onClick={() => setIsFullText(false)} className="mt-2">
              <Text>
                <FormattedMessage defaultMessage="Read less" id="Ed2ywJ" />
              </Text>
            </button>
          )}
        </div>
      )}
      {title && <SectionList title={title} />}
    </div>
  );
});

HowToOutdoorsy.displayName = 'HowToOutdoorsy';

export default HowToOutdoorsy;
